import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { Control, Controller, FieldPath, FieldValues, UseFormRegister } from 'react-hook-form';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps<TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  register?: UseFormRegister<TFieldValues>;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  options: SelectOption[];
  displayEmpty?: boolean;
  emptyLabel?: string;
  error?: boolean;
}

export function Select<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  register,
  label,
  disabled,
  required,
  helperText,
  options,
  displayEmpty,
  emptyLabel = 'Select an option',
  error,
  ...selectProps
}: SelectProps<TFieldValues> & Omit<MuiSelectProps, 'name' | 'control' | 'error' | 'helperText' | 'label' | 'displayEmpty'>) {
  const labelId = `${name}-label`;

  if (register) {
    // Use register pattern
    const { onChange, onBlur, name: fieldName, ref } = register(name);
    return (
      <FormControl fullWidth error={error}>
        {label && (
          <InputLabel id={labelId}>
            {label}
            {required && ' *'}
          </InputLabel>
        )}
        <MuiSelect
          {...selectProps}
          name={fieldName}
          inputRef={ref}
          onChange={onChange}
          onBlur={onBlur}
          labelId={labelId}
          label={label}
          disabled={disabled}
          displayEmpty={displayEmpty}
        >
          {displayEmpty && (
            <MenuItem value="">
              <em>{emptyLabel}</em>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    );
  }

  if (!control) {
    throw new Error('Either control or register must be provided to Select');
  }

  // Use Controller pattern
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl fullWidth error={!!fieldState.error}>
          {label && (
            <InputLabel id={labelId}>
              {label}
              {required && ' *'}
            </InputLabel>
          )}
          <MuiSelect
            {...field}
            {...selectProps}
            labelId={labelId}
            label={label}
            disabled={disabled}
            displayEmpty={displayEmpty}
          >
            {displayEmpty && (
              <MenuItem value="">
                <em>{emptyLabel}</em>
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </MenuItem>
            ))}
          </MuiSelect>
          <FormHelperText>{fieldState.error?.message || helperText}</FormHelperText>
        </FormControl>
      )}
    />
  );
}